import { Row } from "react-grid-system";
import {
  HashRouter,
  Navigate,
  Outlet,
  Route,
  Routes as Switch,
} from "react-router-dom";

import React, { PropsWithChildren, ReactElement, Suspense, lazy } from "react";

import Loader from "components/Common/Loader";
import { ParticipantProvider } from "components/Meeting/Live/ParticipantProvider";
import VideoProvider from "components/Meeting/Live/VideoProvider";

import { selectAuth } from "store/auth/selectors";
import { User } from "store/auth/types";
import { useAppSelector } from "store/hooks";

import routes from "./routes";

const Login = lazy(() => import("pages/Auth/Login"));
const Home = lazy(() => import("pages/Main"));
const Calendar = lazy(() => import("pages/Calendar"));
const Meeting = lazy(() => import("pages/Meetings/Meeting"));
const MeetingSummary = lazy(() => import("pages/Meetings/Summary"));
const ArchivedMeetings = lazy(() => import("pages/Meetings/Archive"));
const AddMeeting = lazy(() => import("pages/Meetings/Form"));
const Documents = lazy(() => import("pages/Documents"));
const Messages = lazy(() => import("pages/Messages"));
const Votings = lazy(() => import("pages/Votings/List"));
const Voting = lazy(() => import("pages/Votings/Voting"));
const Workspaces = lazy(() => import("pages/Workspaces/List"));
const Workspace = lazy(() => import("pages/Workspaces/Workspace"));
const Reset = lazy(() => import("pages/Auth/ResetPassword"));
const Settings = lazy(() => import("pages/Settings"));
const RolesAndPermissions = lazy(() => import("pages/RolesAndPermissions"));
const Users = lazy(() => import("pages/Users/List"));
const EditUser = lazy(() => import("pages/Users/Form"));
const CompanyForm = lazy(() => import("pages/Company/Form"));
const WorkspaceForm = lazy(() => import("pages/Workspaces/Form"));
const Tasks = lazy(() => import("pages/Tasks/List"));
const TaskForm = lazy(() => import("pages/Tasks/Form"));
const LiveMeeting = lazy(() => import("pages/Meetings/LiveMeeting"));
const OfflineMeeting = lazy(() => import("pages/Meetings/OfflineMeeting"));
const LiveVoting = lazy(() => import("pages/Votings/LiveVoting"));
const OfflineVoting = lazy(() => import("pages/Votings/OfflineVoting"));
const Task = lazy(() => import("pages/Tasks/Task"));
const GlobalTask = lazy(() => import("pages/Tasks/Task"));
const VotingForm = lazy(() => import("pages/Votings/Form"));
const VotingSummary = lazy(() => import("pages/Votings/Summary"));
const ErrorPage = lazy(() => import("pages/404"));
const InvitationsAction = lazy(() => import("pages/Meetings/Invitation"));

const ProtectedRoute = ({
  user,
  redirectPath = "/login",
}: {
  user: User | null;
  redirectPath?: string;
}) => {
  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};

const Routes: React.FC<PropsWithChildren> = ({ children }): ReactElement => {
  const {
    home,
    auth,
    calendar,
    meetings,
    documents,
    messages,
    votings,
    tasks,
    workspaces,
    settings,
    users,
    company,
  } = routes;

  const authState = useAppSelector(selectAuth);

  return (
    <HashRouter>
      <VideoProvider>
        <ParticipantProvider>
          <Suspense
            fallback={
              <Row justify="center" align="center" style={{ height: "100vh" }}>
                <Loader />
              </Row>
            }
          >
            <Switch>
              <Route path={auth.login} element={<Login />} />
              <Route path={auth.resetPassword} element={<Reset />} />
              <Route path={auth.firstLogin} element={<Reset />} />
              <Route
                path={meetings.invitations.reviewersByMain}
                element={<InvitationsAction />}
              />
              <Route
                path={meetings.invitations.presenceByMail}
                element={<InvitationsAction />}
              />
              <Route element={<ProtectedRoute user={authState.user} />}>
                <Route path={home} element={<Home />} />
                <Route path={calendar} element={<Calendar />} />
                <Route path={meetings.item} element={<Meeting />} />
                <Route
                  path={meetings.summaryItem}
                  element={<MeetingSummary />}
                />
                <Route path={meetings.add} element={<AddMeeting />} />
                <Route path={meetings.archive} element={<ArchivedMeetings />} />
                <Route path={meetings.edit} element={<AddMeeting />} />
                <Route path={meetings.liveItem} element={<LiveMeeting />} />
                <Route path={meetings.offline} element={<OfflineMeeting />} />
                <Route path={votings.liveItem} element={<LiveVoting />} />
                <Route path={votings.offlineItem} element={<OfflineVoting />} />
                <Route path={documents} element={<Documents />} />
                <Route path={messages} element={<Messages />} />
                <Route path={votings.list} element={<Votings />} />
                <Route path={votings.item} element={<Voting />} />
                <Route path={votings.add} element={<VotingForm />} />
                <Route path={votings.edit} element={<VotingForm />} />
                <Route path={votings.summaryItem} element={<VotingSummary />} />
                <Route path={tasks.list} element={<Tasks />} />
                <Route path={tasks.add} element={<TaskForm />} />
                <Route path={tasks.item} element={<Task />} />
                <Route path={tasks.globalItem} element={<GlobalTask />} />
                <Route path={tasks.edit} element={<TaskForm />} />
                <Route path={workspaces.list} element={<Workspaces />} />
                <Route path={workspaces.item} element={<Workspace />} />
                <Route
                  path={workspaces.createWorkspace}
                  element={<WorkspaceForm />}
                />
                <Route path={workspaces.edit} element={<WorkspaceForm />} />

                <Route path={settings.page} element={<Settings />} />
                <Route path={company.form} element={<CompanyForm />} />
                <Route
                  path={settings.rolesAndPermission}
                  element={<RolesAndPermissions />}
                />
                <Route path={users.list} element={<Users />} />
                <Route path={users.edit} element={<EditUser />} />
                <Route path={users.add} element={<EditUser />} />
              </Route>

              <Route path="*" element={<ErrorPage />} />
            </Switch>
          </Suspense>
          {children}
        </ParticipantProvider>
      </VideoProvider>
    </HashRouter>
  );
};

export default Routes;
